import { useMemo } from "react";
import useResizeObserver from "use-resize-observer";
import { defaultMargin } from "./TimeSeriesChart.helpers";
import {
  ChartTheme,
  DataPoint,
  TimeSeriesChartProps,
} from "./TimeSeriesChart.types";
import { scaleLinear, scaleTime } from "@visx/scale";
import { addDays } from "date-fns";
import { useTheme } from "@mui/material";
import { defaultStyles as defaultTooltipStyles } from "@visx/tooltip";

export const useTimeSeriesChart = <T extends DataPoint>({
  data = [],
  xAccessor,
  yAccessor,
  width: widthProp,
  height: heightProp,
  margin = defaultMargin,
  showGridRows = true,
  showPoints = true,
  showArea = true,
  numYTicks = 5,
}: TimeSeriesChartProps<T>) => {
  const theme = useTheme();

  // Add resize observer to make the chart responsive
  const { ref: rootRef, width = 1, height = 1 } = useResizeObserver();

  const chartTheme: ChartTheme = {
    background: "#ffffff",
    area: "#f7fcfa",
    line: {
      color: theme.palette.primary.main,
      width: 2,
    },
    gradient: {
      from: theme.palette.primary.light,
      to: theme.palette.primary.lighter,
      fromOpacity: 0.2,
      toOpacity: 0.05,
    },
    grid: theme.palette.grey[30],
    axis: theme.palette.grey[30],
    tickLabel: theme.palette.grey[90],
    point: {
      color: theme.palette.primary.main,
      radius: 4,
    },
    tooltip: {
      background: theme.palette.grey[100],
      color: "white",
      fontSize: "14px",
      borderRadius: "4px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  };

  const tooltipStyles = {
    ...defaultTooltipStyles,
    backgroundColor: chartTheme.tooltip.background,
    color: chartTheme.tooltip.color,
    fontSize: chartTheme.tooltip.fontSize,
    borderRadius: chartTheme.tooltip.borderRadius,
    boxShadow: chartTheme.tooltip.boxShadow,
    whiteSpace: "nowrap" as const,
  };

  const rootStyle = {
    width: widthProp,
    height: heightProp,
  };

  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // --- Data Processing ---
  const formattedData = useMemo(() => {
    if (!data || data.length === 0 || !xAccessor || !yAccessor) return [];

    return data
      .map((d) => {
        const rawDate = xAccessor(d);
        return {
          ...d,
          date: typeof rawDate === "number" ? new Date(rawDate) : rawDate,
          value: yAccessor(d),
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [data, xAccessor, yAccessor]);

  const xScale = useMemo(() => {
    const domain =
      formattedData.length > 0
        ? [formattedData[0].date, formattedData[formattedData.length - 1].date]
        : [new Date(), addDays(new Date(), 1)];

    return scaleTime({
      range: [0, innerWidth],
      domain: domain,
    });
  }, [formattedData, innerWidth]);

  const yScale = useMemo(() => {
    if (formattedData.length > 0) {
      const maxVal = Math.max(0, ...formattedData.map((d) => d.value));
      const minVal = Math.min(0, ...formattedData.map((d) => d.value));

      return scaleLinear({
        range: [innerHeight, 0],
        domain: [minVal, maxVal],
        nice: true,
      });
    } else {
      return scaleLinear({
        range: [innerHeight, 0],
        domain: [0, 100],
        nice: true,
      });
    }
  }, [formattedData, innerHeight]);

  return {
    rootRef,
    rootStyle,
    innerWidth,
    innerHeight,
    formattedData,
    xScale,
    yScale,
    margin,
    height,
    width,
    showArea,
    showGridRows,
    showPoints,
    numYTicks,
    chartTheme,
    tooltipStyles,
  };
};
